// Get Involved section content.
// - Tour de Farm 2026 details come from worldfarmers.org/tour-de-farm (10th Annual
//   Farm Tour, July 31, 2026). Schedule/agenda items are illustrative placeholders.
// - JOBS and PARTNERSHIPS are DUMMY posts to be replaced with real openings.
// - Registration, application, and inquiry forms are MOCKED (no backend yet).

import { ORG } from "@/content/worldFarmers";
import { CONTACT_INFO } from "@/content/contact";

export const GET_INVOLVED_HERO = {
  eyebrow: "Get involved",
  title: "There are many ways to stand with our farmers",
  lede:
    "Give, visit the fields, or bring your organization to the table. Every form of support helps immigrant and refugee farmers build businesses of their own.",
  image: "/images/wf/vlai/vlai-3596.jpg",
  imageAlt: "A farmer in a floral sun hat picks peppers from a dense green plant.",
};

export const GET_INVOLVED_PATHS = [
  {
    id: "donate",
    title: "Donate",
    body: "Fund land access, training, tools, and market infrastructure. Give online, by check, or in-kind.",
    href: "/support",
    cta: "Give today",
    image: "/images/wf/vlai/vlai-3497.jpg",
    alt: "A farmer in a white head wrap harvests greens inside a sunlit high tunnel.",
  },
  {
    id: "events",
    title: "Attend an event",
    body: "Walk the land and meet the farmers at Tour de Farm, harvest celebrations, and community gatherings.",
    href: "/events",
    cta: "See upcoming events",
    image: "/images/wf/vlai/vlai-3472.jpg",
    alt: "A farmer seen through the open doorway of a wooden hoop house at the Flats Mentor Farm.",
  },
  {
    id: "work-with-us",
    title: "Work with us",
    body: "Join our team, or partner with World Farmers as a landowner, buyer, funder, or community organization.",
    href: "/work-with-us",
    cta: "Jobs & partnerships",
    image: "/images/wf/vlai/vlai-3870.jpg",
    alt: "A farmer pushes a wheelbarrow of harvested greens along a farm road.",
  },
  {
    id: "contact",
    title: "Contact us",
    body: "Questions, media inquiries, or just want to say hello? Our office in Lancaster is here to help.",
    href: "/contact",
    cta: "Get in touch",
    image: "/images/wf/vlai/vlai-3575.jpg",
    alt: "A farmer walks between rows of crops on a sunny afternoon.",
  },
];

export const GET_INVOLVED_WAYS = {
  eyebrow: "Other ways to help",
  title: "Small actions, real impact",
  items: [
    { id: "shop", title: "Shop with our farmers", body: "Buy at a farmers\u2019 market or join the CSA \u2014 the most direct way to support a farmer.", href: "/where-to-buy", cta: "Where to buy" },
    { id: "wishlist", title: "Donate tools & equipment", body: "BCS tillers, cultivators, market tents, hoses, hoes and shovels are always on our wish list.", href: "/support", cta: "See the wish list" },
    { id: "spread", title: "Spread the word", body: "Follow along, share our stories, and bring a friend to the next farm tour.", href: "/media", cta: "Latest news" },
  ],
};

export const EMAIL_LIST = {
  eyebrow: "Join our email list",
  title: "Stay in touch with World Farmers",
  body:
    "Be the first to know, and sign up to receive World Farmers communications. Emails will not be used for any other purpose than newsletters, CSA and market announcements, and general updates on World Farmers and our programs.",
  interests: ["Newsletter", "CSA & market announcements", "Events", "Partnership opportunities"],
};

/* ---------------- Events ---------------- */
export const EVENTS_HERO = {
  eyebrow: "",
  title: "Events",
  lede:
    "From our annual Tour de Farm to harvest celebrations and farmer trainings, our events bring the community together on the land in Lancaster and beyond.",
  image: "/images/wf/vlai/vlai-3786.jpg",
  imageAlt: "A farmer carries freshly harvested gourds through the field.",
};

export const EVENTS = [
  {
    slug: "tour-de-farm-2026",
    title: "Tour de Farm 2026",
    subtitle: "10th Annual Farm Tour",
    category: "Farm tour",
    date: "2026-07-31",
    dateLabel: "Friday, July 31, 2026",
    dateParts: { month: "JUL", day: "31", year: "2026" },
    time: "10:00 AM \u2013 2:00 PM",
    startTime: "10:00",
    endTime: "14:00",
    location: "Flats Mentor Farm",
    address: "769 Main Street, Lancaster, MA 01523",
    cost: "Free \u00b7 Registration requested",
    featured: true,
    summary:
      "Save the date! Walk the land, meet the farmers, and see sustainable agriculture in action at our 10th annual community farm tour.",
    description: [
      "Tour de Farm is World Farmers\u2019 signature community gathering. Each summer we open the gates of the Flats Mentor Farm so friends, neighbors, partners, and supporters can walk the 70-acre river-bottom parcel where more than 250 immigrant and refugee farmers grow crops from every corner of the globe.",
      "Guided walks are led by World Farmers staff and the farmers themselves. You\u2019ll see high tunnels and irrigation in action, learn how the five-phase mentorship program works, and hear first-hand what it takes to build an independent farm business in New England.",
      "The tour wraps with a tasting of just-harvested produce and the chance to buy directly from farmers at a pop-up market. Wear closed-toe shoes and bring water and sun protection \u2014 we\u2019ll be in the fields.",
    ],
    schedule: [
      { time: "10:00 AM", title: "Welcome & check-in", body: "Meet at the World Farmers office tent at the farm entrance." },
      { time: "10:30 AM", title: "Guided field walks", body: "Small groups tour plots, high tunnels, and the wash & pack area with staff and farmers." },
      { time: "12:00 PM", title: "Harvest tasting", body: "Sample cultural crops prepared by farmers and partner chefs." },
      { time: "1:00 PM", title: "Pop-up farmers\u2019 market", body: "Buy directly from the farmers you met on the walk." },
    ],
    highlights: ["Guided walks with farmers", "High tunnels & irrigation", "Harvest tasting", "Pop-up market", "Family friendly"],
    image: "/images/wf/vlai/vlai-3735.jpg",
    imageAlt: "Pepper plants heavy with ripening fruit.",
    gallery: ["/images/wf/vlai/vlai-3765.jpg", "/images/wf/vlai/vlai-3786.jpg", "/images/wf/farm-007.jpg"],
    registration: { open: true, note: "Online ticketing platform coming soon \u2014 reserve your spot below and we\u2019ll confirm by email." },
    contact: { name: "Contact the World Farmers Team", email: CONTACT_INFO.email, phone: ORG.phone },
  },
];

export const EVENT_TYPES = ["Farm tour", "Harvest celebration", "Farmer training", "Community"];

/* ---------------- Work with us ---------------- */
export const WORK_HERO = {
  eyebrow: "",
  title: "Work With Us",
  lede:
    "World Farmers is a small, mission-driven team based in Lancaster, MA. We hire people who love the land and the people who farm it \u2014 and we partner with organizations that share that commitment.",
  image: "/images/wf/vlai/vlai-3532.jpg",
  imageAlt: "A farmer in a floral sun hat carries an armful of greens through the field.",
};

export const WORK_VALUES = [
  { title: "Farmer-first", body: "Every decision starts with what helps a farmer succeed." },
  { title: "Cross-cultural", body: "Our team and farmers speak dozens of languages; we learn from each other daily." },
  { title: "Hands in the soil", body: "Office days end in the fields. Expect mud on your boots." },
];

// DUMMY job posts.
export const JOBS = [
  {
    id: "farm-manager",
    title: "Assistant Farm Manager",
    type: "Full-time",
    location: "Lancaster, MA (on-site)",
    department: "Farm operations",
    posted: "Posted 2 weeks ago",
    summary: "Support day-to-day operations at the Flats Mentor Farm: irrigation, high tunnels, equipment, and farmer support across the growing season.",
    description: [
      "Placeholder description. The Assistant Farm Manager works alongside the Farm Manager to keep the 70-acre Flats Mentor Farm running smoothly \u2014 maintaining irrigation systems and high tunnels, coordinating shared equipment, and supporting farmers with hands-on technical assistance.",
      "You\u2019ll help deliver field trainings, track plot assignments, and ensure food-safety practices are followed at the wash & pack station. Seasonal hours are long from May through October and lighter in winter, when the focus shifts to planning and infrastructure projects.",
    ],
    responsibilities: ["Maintain irrigation, tunnels, and shared equipment", "Support farmers with technical assistance in the field", "Coordinate plot assignments and season set-up", "Assist with food-safety and wash/pack operations"],
    qualifications: ["2+ seasons of vegetable production experience", "Comfort operating tractors and small equipment", "Patience and cultural humility working across languages", "Valid driver\u2019s license"],
    compensation: "$48,000 \u2013 $55,000 + benefits",
  },
  {
    id: "csa-coordinator",
    title: "CSA & Markets Coordinator",
    type: "Full-time \u00b7 Seasonal",
    location: "Lancaster, MA + market sites",
    department: "Markets",
    posted: "Posted 3 weeks ago",
    summary: "Run the World Farmers CSA and support farmers at 19+ farmers\u2019 markets across Massachusetts.",
    description: [
      "Placeholder description. The CSA & Markets Coordinator manages member sign-ups, weekly share aggregation, and Thursday pickups across four locations, while coordinating market schedules, SNAP/HIP compliance, and signage for farmers selling at markets.",
      "This role is a great fit for someone who loves logistics, spreadsheets, and people in equal measure.",
    ],
    responsibilities: ["Manage CSA memberships, payments, and pickups", "Coordinate weekly share aggregation with farmers", "Support market vendors with SNAP/HIP and permits", "Track sales data and member feedback"],
    qualifications: ["Experience in food access, farmers\u2019 markets, or CSA operations", "Strong organization and communication skills", "Spanish, Swahili, or Hmong a plus", "Ability to lift 40 lbs and work outdoors"],
    compensation: "$22 \u2013 $26 / hour",
  },
  {
    id: "development-associate",
    title: "Development & Communications Associate",
    type: "Part-time",
    location: "Hybrid \u00b7 Lancaster, MA",
    department: "Development",
    posted: "Posted 1 month ago",
    summary: "Tell the World Farmers story to donors, partners, and the press, and help grow individual giving.",
    description: [
      "Placeholder description. The Development & Communications Associate supports grant reporting, donor stewardship, newsletters, social media, and event promotion \u2014 including Tour de Farm.",
    ],
    responsibilities: ["Draft newsletters, social posts, and press materials", "Support grant reports and donor acknowledgments", "Maintain the donor database", "Help plan and promote events"],
    qualifications: ["Excellent writing skills", "Experience with email marketing and CRM tools", "Photography or design skills a plus"],
    compensation: "$25 \u2013 $30 / hour \u00b7 20 hrs/week",
  },
  {
    id: "field-intern",
    title: "Summer Field Intern",
    type: "Internship \u00b7 Seasonal",
    location: "Lancaster, MA (on-site)",
    department: "Farm operations",
    posted: "Applications open",
    summary: "Spend a season in the fields learning alongside immigrant and refugee farmers.",
    description: [
      "Placeholder description. Field interns support farmers and staff with planting, irrigation, harvest, and market prep while learning sustainable production and cross-cultural farmer education.",
    ],
    responsibilities: ["Assist with field work and harvest", "Support farmer trainings and farm tours", "Help staff the CSA pickup"],
    qualifications: ["Interest in sustainable agriculture and food justice", "Ability to work outdoors in all weather", "Students and recent graduates encouraged"],
    compensation: "Stipend \u00b7 May \u2013 September",
  },
];

// DUMMY partnership opportunities.
export const PARTNERSHIPS = [
  {
    id: "land",
    title: "Landowners & land trusts",
    type: "Land access",
    summary: "Have farmland in Massachusetts? Lease or donate land so more farmers can move toward independent operations.",
    description: [
      "Placeholder description. Access to land is the single greatest barrier for our farmers. We partner with private landowners, towns, and conservation organizations to bring additional acres into production under long-term, farmer-friendly agreements.",
      "World Farmers handles stewardship planning, insurance, and farmer matching so landowners can see their land farmed well.",
    ],
    looking: ["5+ acres of tillable land within 60 miles of Lancaster", "Access to water", "Multi-year lease or conservation partnership"],
    icon: "land",
  },
  {
    id: "buyers",
    title: "Wholesale buyers & CSA partners",
    type: "Markets",
    summary: "Grocers, schools, hospitals, and businesses: source Massachusetts-grown mainstream & culturally relevant crops, or partner as a CSA pick-up location.",
    description: [
      "Placeholder description. We coordinate aggregated harvests from 260+ farmers so institutional buyers receive consistent volume of hard-to-find crops with a single point of contact.",
    ],
    looking: ["Seasonal or standing orders June \u2013 October", "Interest in culturally relevant produce", "Food access and community programs welcome"],
    icon: "buyers",
  },
  {
    id: "funders",
    title: "Funders & corporate sponsors",
    type: "Philanthropy",
    summary: "Sponsor World Farmers events, contribute to a program that moves you, purchase a CSA for a family in need, or generally invest in our work.",
    description: [
      "Placeholder description. Foundations and companies can support World Farmers through program grants, event sponsorship, matching gifts, and employee engagement days at the Flats.",
    ],
    looking: ["Program and capital grants", "Event sponsorships", "Employee giving and engagement days"],
    icon: "funders",
  },
  {
    id: "community",
    title: "Community Organizations",
    type: "Community",
    summary: "Have an idea for an initiative that can mutually benefit the communities we both serve? Reach out and let\u2019s explore!",
    description: [
      "Placeholder description. Many of our farmers learn about the Flats through their churches and community groups. We partner with organizations to reach new farmers, host pickups, and organize workdays.",
    ],
    looking: ["CSA pickup host sites", "Farmer referral partners", "Group farm visits"],
    icon: "community",
  },
];

export const WORK_CONTACT = {
  email: CONTACT_INFO.email,
  phone: ORG.phone,
  phoneHref: ORG.phoneHref,
  eeo: "World Farmers is an equal opportunity employer. We celebrate diversity and are committed to creating an inclusive environment for all employees, farmers, and partners.",
};
